// AIDialerCampaign.jsx — Phase 3B AI Dialer Campaign Detail/Builder
// 6-step wizard + live call feed

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight, ChevronLeft, Users, Mic2, FileText, ShieldCheck,
  Clock, Rocket, Phone, CheckCircle2, Loader2, Play, Pause,
  PhoneCall, BookOpen, XCircle, AlertCircle, RefreshCw, Plus, Trash2,
  DollarSign, Bot, Volume2, BarChart3,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { apiGet, apiPost, apiPut } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';

const WIZARD_STEPS = [
  { id: 'leads',    label: 'Leads',    icon: Users },
  { id: 'voice',    label: 'Voice',    icon: Mic2 },
  { id: 'script',   label: 'Script',   icon: FileText },
  { id: 'dnc',      label: 'DNC',      icon: ShieldCheck },
  { id: 'schedule', label: 'Schedule', icon: Clock },
  { id: 'launch',   label: 'Launch',   icon: Rocket },
];

const US_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const OUTCOME_CONFIG = {
  booked_appointment: { label: 'Booked', color: 'text-civix-cyan',  bg: 'bg-cyan-500/10' },
  not_interested:     { label: 'Not Interested', color: 'text-red-400',    bg: 'bg-red-400/10'  },
  callback_later:     { label: 'Callback',       color: 'text-yellow-400', bg: 'bg-yellow-400/10' },
  wrong_number:       { label: 'Wrong #',        color: 'text-white/40',   bg: 'bg-white/5'    },
  dnc_blocked:        { label: 'DNC',            color: 'text-orange-400', bg: 'bg-orange-400/10' },
  failed:             { label: 'Failed',         color: 'text-red-500',    bg: 'bg-red-500/10'   },
};

function fmt(v) {
  if (v == null || isNaN(v)) return '$0.00';
  if (v < 0.01 && v > 0) return '<$0.01';
  return `$${Number(v).toFixed(2)}`;
}

export default function AIDialerCampaign() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [wizardStep, setWizardStep] = useState(0);
  const [tab, setTab] = useState('build'); // 'build' | 'live'

  // Phase 2: Bot selector state
  const [selectedBotId, setSelectedBotId] = useState('');

  // Form state
  const [form, setForm] = useState({
    name: '',
    leadIds: [],
    agentVoiceId: '',
    botId: '',
    script: {
      intro: "Hi, this is {{agentName}} calling from {{company}}. I wanted to follow up about your recent inquiry about insurance coverage.",
      qualifyingQuestions: [
        "Are you still looking for insurance coverage?",
        "What type of coverage are you most interested in?",
        "When would be a good time to have a quick call to go over your options?",
      ],
      closingPitch: "I'd love to set up a quick 15-minute call to go over your options. I have some time available this week — would any of those work for you?",
      calendarLink: '',
    },
    dncSettings: { skipFederal: false, skipState: false, skipInternal: false, skipDoNotContact: false },
    schedule: {
      sendDays: [1, 2, 3, 4, 5],
      sendWindowStart: '09:00',
      sendWindowEnd: '18:00',
      timezone: 'America/Chicago',
      maxConcurrent: 3,
    },
    disclosureRequired: true,
    recordCalls: true,
  });

  const [leadFilter, setLeadFilter] = useState({ state: '', limit: 50 });
  const [estimateResult, setEstimateResult] = useState(null);

  // Fetch campaign
  const { data: campaignData, isLoading, refetch } = useQuery({
    queryKey: ['ai-dialer-campaign', id],
    queryFn: () => apiGet(`/ai-dialer/campaigns/${id}`),
    staleTime: 5000,
    refetchInterval: (data) => {
      const status = data?.data?.status || data?.status;
      return status === 'active' ? 5000 : 30000;
    },
  });

  const campaign = campaignData?.data || campaignData || {};

  // Sync form from campaign
  useEffect(() => {
    if (campaign._id) {
      setForm((prev) => ({
        ...prev,
        name:     campaign.name     || prev.name,
        leadIds:  campaign.leadIds  || [],
        agentVoiceId: String(campaign.agentVoiceId?._id || campaign.agentVoiceId || ''),
        botId: String(campaign.botId?._id || campaign.botId || ''),
        script:   { ...prev.script, ...campaign.script },
        dncSettings: { ...prev.dncSettings, ...campaign.dncSettings },
        schedule:    { ...prev.schedule, ...campaign.schedule },
        disclosureRequired: campaign.disclosureRequired !== undefined ? campaign.disclosureRequired : true,
        recordCalls: campaign.recordCalls !== undefined ? campaign.recordCalls : true,
      }));
      // If campaign is running, show live tab
      if (campaign.status === 'active') setTab('live');
    }
  }, [campaign._id]);

  // Fetch leads for selection
  const { data: leadsData } = useQuery({
    queryKey: ['leads-for-dialer', leadFilter],
    queryFn: () => apiGet('/leads', { limit: 100, state: leadFilter.state || undefined }),
    staleTime: 30000,
  });
  const leads = leadsData?.leads || leadsData?.data || [];

  // Phase 2: Fetch bots for selector
  const { data: botsData } = useQuery({
    queryKey: ['bots-for-dialer'],
    queryFn: () => apiGet('/bots', { type: 'voice', status: 'ready' }),
    staleTime: 30000,
  });
  const availableBots = botsData?.data || [];

  // When a bot is selected, pre-fill script from bot
  const selectedBot = availableBots.find((b) => b._id === (form.botId || selectedBotId));

  // Fetch voice clones
  const { data: voicesData } = useQuery({
    queryKey: ['voice-clones-list'],
    queryFn: () => apiGet('/voice-cloning/list'),
    staleTime: 30000,
  });
  const voices = voicesData?.data || voicesData || [];

  // Fetch calls for live feed
  const { data: callsData, refetch: refetchCalls } = useQuery({
    queryKey: ['ai-dialer-calls', id],
    queryFn: () => apiGet('/ai-dialer/calls', { campaignId: id, limit: 20 }),
    staleTime: 3000,
    refetchInterval: campaign.status === 'active' ? 3000 : 10000,
  });
  const calls = callsData?.data || callsData || [];

  // Fetch cost estimate (Phase 4B: use per-campaign endpoint)
  async function fetchEstimate() {
    try {
      const r = await apiGet(`/ai-dialer/campaigns/${id}/cost-estimate`);
      setEstimateResult(r?.data || r);
    } catch {
      // fallback to generic estimate
      const leadCount = form.leadIds.length || 1;
      const r = await apiPost('/ai-dialer/estimate-cost', { leadCount, avgCallMinutes: 5 });
      setEstimateResult(r?.data || r);
    }
  }

  // ─── Mutations ────────────────────────────────────────────────────────────

  const saveMutation = useMutation({
    mutationFn: (data) => apiPut(`/ai-dialer/campaigns/${id}`, data),
    onSuccess: () => { toast.success('Campaign saved'); refetch(); },
    onError: (e) => toast.error(e.response?.data?.error || 'Save failed'),
  });

  const startMutation = useMutation({
    mutationFn: () => apiPost(`/ai-dialer/campaigns/${id}/start`, {}),
    onSuccess: () => { toast.success('Campaign started! AI is dialing...'); refetch(); setTab('live'); },
    onError: (e) => toast.error(e.response?.data?.error || 'Failed to start'),
  });

  const pauseMutation = useMutation({
    mutationFn: () => apiPost(`/ai-dialer/campaigns/${id}/pause`, {}),
    onSuccess: () => { toast.success('Campaign paused'); refetch(); },
    onError: (e) => toast.error(e.response?.data?.error || 'Pause failed'),
  });

  function saveAndNext() {
    saveMutation.mutate(form);
    setWizardStep((s) => Math.min(s + 1, WIZARD_STEPS.length - 1));
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <TopBar title="AI Dialer Campaign" subtitle="AI Dialer" />
        <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 text-civix-purple animate-spin" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <TopBar
        title={campaign.name || 'New Campaign'}
        subtitle={`AI Dialer · ${campaign.status || 'draft'}`}
        actions={
          <div className="flex gap-2">
            {campaign.status === 'active' && (
              <button onClick={() => pauseMutation.mutate()} disabled={pauseMutation.isPending} className="btn-ghost text-yellow-400 text-sm flex items-center gap-1">
                <Pause className="w-3.5 h-3.5" /> Pause
              </button>
            )}
            <button onClick={() => nav('/ai-dialer')} className="btn-ghost text-sm">← Back</button>
          </div>
        }
      />

      {/* Tab bar */}
      <div className="panel p-2 flex gap-1">
        {['build', 'live'].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
              tab === t ? 'bg-civix-purple/20 text-white border border-civix-purple/40' : 'text-white/50 hover:text-white'
            }`}>
            {t === 'build' ? '⚙ Build' : '📞 Live Feed'}
            {t === 'live' && campaign.status === 'active' && (
              <span className="ml-2 w-1.5 h-1.5 rounded-full bg-green-400 inline-block animate-pulse" />
            )}
          </button>
        ))}
      </div>

      {/* Phase 2: Use Bot Banner */}
      {tab === 'build' && (
        <div className="panel p-4 flex flex-wrap items-center gap-3">
          <Bot className="w-4 h-4 text-civix-purple shrink-0" />
          <span className="text-sm text-white/70 font-medium">Use Bot:</span>
          <select
            className="input flex-1 min-w-0 max-w-xs text-sm"
            value={form.botId || ''}
            onChange={(e) => {
              const botId = e.target.value;
              const bot = availableBots.find((b) => b._id === botId);
              setForm((f) => ({
                ...f,
                botId,
                ...(bot?.script && Object.keys(bot.script).length > 0
                  ? { script: { ...f.script, ...bot.script } }
                  : {}),
              }));
            }}
          >
            <option value="">No bot — manual script</option>
            {availableBots.map((b) => (
              <option key={b._id} value={b._id}>{b.name}</option>
            ))}
          </select>
          {selectedBot && (
            <span className="text-xs px-2.5 py-1 rounded-full bg-civix-purple/20 text-civix-purple flex items-center gap-1.5">
              <CheckCircle2 className="w-3 h-3" />
              Using persona: {selectedBot.name}
            </span>
          )}
        </div>
      )}

      {/* BUILD TAB */}
      {tab === 'build' && (
        <div className="grid lg:grid-cols-[240px_1fr] gap-4">
          {/* Step sidebar */}
          <div className="panel p-3">
            {WIZARD_STEPS.map((s, i) => {
              const Icon = s.icon;
              const active   = i === wizardStep;
              const complete = i < wizardStep;
              return (
                <button key={s.id} onClick={() => setWizardStep(i)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium mb-1 transition-all ${
                    active   ? 'bg-civix-purple/20 text-white border border-civix-purple/40' :
                    complete ? 'text-civix-cyan hover:bg-civix-panelLight' :
                               'text-white/40 hover:text-white/60'
                  }`}>
                  {complete ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <Icon className="w-4 h-4 shrink-0" />}
                  {s.label}
                </button>
              );
            })}
          </div>

          {/* Step content */}
          <div className="panel p-6">

            {/* Step 0: Leads */}
            {wizardStep === 0 && (
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-white">Select Leads</h3>
                <div className="flex gap-3">
                  <input
                    value={leadFilter.state}
                    onChange={(e) => setLeadFilter({ ...leadFilter, state: e.target.value })}
                    placeholder="Filter by state (e.g. TX)"
                    className="input flex-1"
                  />
                  <button
                    onClick={() => {
                      const allIds = leads.map((l) => l._id);
                      setForm({ ...form, leadIds: allIds });
                    }}
                    className="btn-secondary text-sm"
                  >
                    Select All ({leads.length})
                  </button>
                </div>
                <div className="max-h-80 overflow-y-auto space-y-1 bg-civix-panelLight rounded-xl p-3 border border-civix-border">
                  {leads.map((lead) => {
                    const selected = form.leadIds.includes(lead._id) || form.leadIds.includes(String(lead._id));
                    return (
                      <div key={lead._id}
                        onClick={() => {
                          const id = lead._id;
                          setForm((f) => ({
                            ...f,
                            leadIds: selected
                              ? f.leadIds.filter((x) => String(x) !== String(id))
                              : [...f.leadIds, id],
                          }));
                        }}
                        className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all ${
                          selected ? 'bg-civix-purple/20 border border-civix-purple/30' : 'hover:bg-civix-border'
                        }`}>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 ${
                          selected ? 'bg-civix-purple border-civix-purple' : 'border-white/30'
                        }`}>
                          {selected && <CheckCircle2 className="w-3 h-3 text-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-white truncate">{lead.firstName} {lead.lastName}</div>
                          <div className="text-xs text-white/40">{lead.phone} · {lead.state}</div>
                        </div>
                      </div>
                    );
                  })}
                  {leads.length === 0 && (
                    <div className="text-center py-8">
                      <Users className="w-8 h-8 text-white/20 mx-auto mb-2" />
                      <div className="text-sm font-medium text-white/70 mb-1">No leads match this filter</div>
                      <div className="text-xs text-white/40 max-w-xs mx-auto">
                        Try clearing the state filter, or import leads from the Leads page first.
                      </div>
                    </div>
                  )}
                </div>
                <div className="text-sm text-white/60">{form.leadIds.length} leads selected</div>
                <button onClick={saveAndNext} className="btn-primary flex items-center gap-2">
                  Next: Choose Voice <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Step 1: Voice */}
            {wizardStep === 1 && (
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-white">Choose Voice Clone</h3>
                {voices.length === 0 ? (
                  <div className="text-center py-8 bg-civix-panelLight rounded-xl border border-civix-border">
                    <Mic2 className="w-10 h-10 text-civix-purple/40 mx-auto mb-3" />
                    <p className="text-white/60 text-sm mb-4">No voice clones yet. Create one first.</p>
                    <a href="/voice-cloning" className="btn-primary text-sm">Create Voice Clone</a>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {voices.map((v) => (
                      <div key={v._id}
                        onClick={() => setForm({ ...form, agentVoiceId: v._id, voiceId: v.voiceId })}
                        className={`p-4 rounded-xl border cursor-pointer transition-all ${
                          form.agentVoiceId === String(v._id)
                            ? 'border-civix-purple/60 bg-civix-purple/10'
                            : 'border-civix-border hover:border-civix-purple/30'
                        }`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-civix-gradient flex items-center justify-center">
                            <Volume2 className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-white">{v.name}</div>
                            <div className={`text-xs ${v.status === 'ready' ? 'text-civix-cyan' : 'text-yellow-400'}`}>
                              {v.status}
                            </div>
                          </div>
                          {form.agentVoiceId === String(v._id) && (
                            <CheckCircle2 className="w-5 h-5 text-civix-cyan ml-auto" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex gap-3">
                  <button onClick={() => setWizardStep(0)} className="btn-ghost flex items-center gap-2"><ChevronLeft className="w-4 h-4" /> Back</button>
                  <button onClick={saveAndNext} className="btn-primary flex items-center gap-2">Next: Script <ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}

            {/* Step 2: Script */}
            {wizardStep === 2 && (
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-white">Call Script</h3>

                <div>
                  <label className="form-label">Opening / Intro</label>
                  <textarea
                    value={form.script.intro}
                    onChange={(e) => setForm({ ...form, script: { ...form.script, intro: e.target.value } })}
                    rows={3} className="input w-full"
                    placeholder="Hi, this is {{agentName}} calling from {{company}}..."
                  />
                  <div className="text-xs text-white/40 mt-1">Use {'{{agentName}}'}, {'{{company}}'}, {'{{leadName}}'} as variables</div>
                </div>

                <div>
                  <label className="form-label">Qualifying Questions</label>
                  {form.script.qualifyingQuestions.map((q, i) => (
                    <div key={i} className="flex gap-2 mb-2">
                      <input
                        value={q}
                        onChange={(e) => {
                          const qs = [...form.script.qualifyingQuestions];
                          qs[i] = e.target.value;
                          setForm({ ...form, script: { ...form.script, qualifyingQuestions: qs } });
                        }}
                        className="input flex-1"
                        placeholder={`Question ${i + 1}`}
                      />
                      <button
                        onClick={() => {
                          const qs = form.script.qualifyingQuestions.filter((_, j) => j !== i);
                          setForm({ ...form, script: { ...form.script, qualifyingQuestions: qs } });
                        }}
                        className="btn-ghost text-red-400 p-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => setForm({ ...form, script: { ...form.script, qualifyingQuestions: [...form.script.qualifyingQuestions, ''] } })}
                    className="btn-ghost text-sm flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Add Question
                  </button>
                </div>

                <div>
                  <label className="form-label">Closing / Appointment Pitch</label>
                  <textarea
                    value={form.script.closingPitch}
                    onChange={(e) => setForm({ ...form, script: { ...form.script, closingPitch: e.target.value } })}
                    rows={2} className="input w-full"
                  />
                </div>

                <div>
                  <label className="form-label">Calendar / Booking Link (optional)</label>
                  <input
                    value={form.script.calendarLink || ''}
                    onChange={(e) => setForm({ ...form, script: { ...form.script, calendarLink: e.target.value } })}
                    className="input w-full"
                    placeholder="https://calendly.com/yourname"
                  />
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setWizardStep(1)} className="btn-ghost flex items-center gap-2"><ChevronLeft className="w-4 h-4" /> Back</button>
                  <button onClick={saveAndNext} className="btn-primary flex items-center gap-2">Next: DNC <ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}

            {/* Step 3: DNC */}
            {wizardStep === 3 && (
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-white">DNC Settings</h3>
                <p className="text-white/60 text-sm">These settings determine which DNC lists to check before each dial. Recommended: keep all enabled.</p>

                {[
                  { key: 'skipDoNotContact', label: 'Skip lead.doNotContact flag', rec: 'Always enabled', danger: false },
                  { key: 'skipInternal',    label: 'Skip internal DNC list',       rec: 'Always enabled', danger: false },
                  { key: 'skipFederal',     label: 'Skip Federal DNC registry',    rec: 'Requires paid DNC_API_URL', danger: true },
                  { key: 'skipState',       label: 'Skip State DNC registries',    rec: 'Phase 4 implementation', danger: true },
                ].map(({ key, label, rec, danger }) => (
                  <div key={key} className="flex items-center justify-between p-4 bg-civix-panelLight rounded-xl border border-civix-border">
                    <div>
                      <div className="text-sm font-medium text-white">{label}</div>
                      <div className={`text-xs mt-0.5 ${danger ? 'text-yellow-400' : 'text-white/40'}`}>{rec}</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!form.dncSettings[key]}
                        onChange={(e) => setForm({ ...form, dncSettings: { ...form.dncSettings, [key]: !e.target.checked } })}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5 bg-civix-border rounded-full peer peer-checked:bg-civix-purple transition-colors" />
                      <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                    </label>
                  </div>
                ))}

                <div className="flex items-center justify-between p-4 bg-civix-panelLight rounded-xl border border-civix-border">
                  <div>
                    <div className="text-sm font-medium text-white">AI Disclosure Required</div>
                    <div className="text-xs text-white/40">Required in CA, FL, NY, TX, IL, PA + federal</div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.disclosureRequired}
                      onChange={(e) => setForm({ ...form, disclosureRequired: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-10 h-5 bg-civix-border rounded-full peer peer-checked:bg-civix-purple transition-colors" />
                    <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                  </label>
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setWizardStep(2)} className="btn-ghost flex items-center gap-2"><ChevronLeft className="w-4 h-4" /> Back</button>
                  <button onClick={saveAndNext} className="btn-primary flex items-center gap-2">Next: Schedule <ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}

            {/* Step 4: Schedule */}
            {wizardStep === 4 && (
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-white">Call Schedule</h3>

                <div>
                  <label className="form-label">Calling Days</label>
                  <div className="flex gap-2 flex-wrap">
                    {US_DAYS.map((day, i) => {
                      const active = form.schedule.sendDays.includes(i);
                      return (
                        <button key={i}
                          onClick={() => {
                            const days = active
                              ? form.schedule.sendDays.filter((d) => d !== i)
                              : [...form.schedule.sendDays, i].sort();
                            setForm({ ...form, schedule: { ...form.schedule, sendDays: days } });
                          }}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                            active ? 'bg-civix-purple/20 border-civix-purple/50 text-white' : 'border-civix-border text-white/40 hover:border-civix-border/80'
                          }`}>
                          {day}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Start Time</label>
                    <input type="time" value={form.schedule.sendWindowStart}
                      onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, sendWindowStart: e.target.value } })}
                      className="input w-full" />
                  </div>
                  <div>
                    <label className="form-label">End Time</label>
                    <input type="time" value={form.schedule.sendWindowEnd}
                      onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, sendWindowEnd: e.target.value } })}
                      className="input w-full" />
                  </div>
                </div>

                <div>
                  <label className="form-label">Timezone</label>
                  <select value={form.schedule.timezone}
                    onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, timezone: e.target.value } })}
                    className="input w-full">
                    <option value="America/New_York">Eastern</option>
                    <option value="America/Chicago">Central</option>
                    <option value="America/Denver">Mountain</option>
                    <option value="America/Los_Angeles">Pacific</option>
                    <option value="America/Phoenix">Arizona</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Max Concurrent Calls: {form.schedule.maxConcurrent}</label>
                  <input type="range" min={1} max={10} value={form.schedule.maxConcurrent}
                    onChange={(e) => setForm({ ...form, schedule: { ...form.schedule, maxConcurrent: Number(e.target.value) } })}
                    className="w-full accent-civix-purple" />
                </div>

                <div className="flex gap-3">
                  <button onClick={() => setWizardStep(3)} className="btn-ghost flex items-center gap-2"><ChevronLeft className="w-4 h-4" /> Back</button>
                  <button onClick={() => { fetchEstimate(); saveAndNext(); }} className="btn-primary flex items-center gap-2">Next: Launch <ChevronRight className="w-4 h-4" /></button>
                </div>
              </div>
            )}

            {/* Step 5: Launch */}
            {wizardStep === 5 && (
              <div className="space-y-5">
                <h3 className="text-lg font-semibold text-white">Review & Launch</h3>

                <div className="space-y-3">
                  {[
                    { label: 'Campaign Name', value: campaign.name || form.name || '—' },
                    { label: 'Leads Selected', value: `${form.leadIds.length} leads` },
                    { label: 'Voice Clone', value: form.agentVoiceId ? `Voice ${form.agentVoiceId.slice(-6)}` : 'Not selected' },
                    { label: 'Calling Window', value: `${form.schedule.sendWindowStart}–${form.schedule.sendWindowEnd} (${form.schedule.timezone})` },
                    { label: 'Max Concurrent', value: `${form.schedule.maxConcurrent} simultaneous calls` },
                    { label: 'AI Disclosure', value: form.disclosureRequired ? 'Required' : 'Disabled' },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex items-center justify-between p-3 bg-civix-panelLight rounded-lg border border-civix-border">
                      <span className="text-sm text-white/60">{label}</span>
                      <span className="text-sm font-medium text-white">{value}</span>
                    </div>
                  ))}
                </div>

                {estimateResult && (
                  <div className="bg-civix-teal/5 border border-civix-teal/30 rounded-xl p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-civix-teal" />
                      <span className="text-sm font-semibold text-white">Pre-flight Cost Estimate</span>
                      <span className="ml-auto text-xs text-white/40 cursor-help" title={estimateResult.note || 'Cost estimate'}>ⓘ {estimateResult.note || ''}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-civix-teal">${(estimateResult.estTotalUsd ?? estimateResult.totalLow ?? 0).toFixed(2)}</span>
                      <span className="text-xs text-white/50">{estimateResult.leadCount ?? form.leadIds.length} leads × ~${(estimateResult.estPerCallUsd ?? estimateResult.perCallLow ?? 0.035).toFixed(3)}/call</span>
                    </div>
                    <div className="text-xs text-white/50 flex flex-wrap gap-x-3 gap-y-1 pt-1 border-t border-civix-border/30">
                      {estimateResult.breakdown?.twilioVoice != null && <span>Twilio: ${Number(estimateResult.breakdown.twilioVoice).toFixed(4)}</span>}
                      {estimateResult.breakdown?.openaiChat != null && <span>OpenAI: ${Number(estimateResult.breakdown.openaiChat).toFixed(4)}</span>}
                      {estimateResult.breakdown?.elevenlabsTts != null && <span>ElevenLabs: ${Number(estimateResult.breakdown.elevenlabsTts).toFixed(4)}</span>}
                      {estimateResult.breakdown?.twilioVoice == null && estimateResult.breakdown?.elevenLabsTTS && <span>{estimateResult.breakdown.elevenLabsTTS}</span>}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <button onClick={() => setWizardStep(4)} className="btn-ghost flex items-center gap-2"><ChevronLeft className="w-4 h-4" /> Back</button>
                  {campaign.status === 'draft' || campaign.status === 'paused' ? (
                    <button
                      onClick={() => startMutation.mutate()}
                      disabled={startMutation.isPending}
                      className="btn-primary flex items-center gap-2"
                    >
                      {startMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                      Launch Campaign
                    </button>
                  ) : (
                    <div className="px-4 py-2 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400 text-sm font-medium flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" /> Campaign {campaign.status}
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      {/* LIVE TAB */}
      {tab === 'live' && (
        <div className="space-y-4">
          {/* Stats row */}
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
            {[
              { label: 'Total', value: campaign.stats?.totalLeads || 0 },
              { label: 'Dialed', value: campaign.stats?.dialed || 0 },
              { label: 'Connected', value: campaign.stats?.connected || 0 },
              { label: 'Qualified', value: campaign.stats?.qualified || 0 },
              { label: 'Booked', value: campaign.stats?.booked || 0 },
              { label: 'Cost', value: fmt(campaign.stats?.totalCostUsd) },
            ].map(({ label, value }) => (
              <div key={label} className="panel p-3 text-center">
                <div className="text-lg font-bold text-white">{value}</div>
                <div className="text-xs text-white/40">{label}</div>
              </div>
            ))}
          </div>

          {/* Call feed */}
          <div className="panel p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-white">Recent Calls</h3>
              <button onClick={refetchCalls} className="btn-ghost text-xs flex items-center gap-1">
                <RefreshCw className="w-3 h-3" /> Refresh
              </button>
            </div>

            {calls.length === 0 ? (
              <div className="text-center py-10">
                <PhoneCall className="w-10 h-10 text-civix-purple/40 mx-auto mb-3" />
                <div className="text-sm font-medium text-white/70 mb-1">No calls yet</div>
                <div className="text-xs text-white/40 max-w-xs mx-auto">
                  {campaign.status === 'active'
                    ? 'Dialer is warming up — the first call will appear here within a minute.'
                    : 'Launch the campaign from the Build tab to start dialing leads.'}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {calls.map((call) => {
                  const oc = OUTCOME_CONFIG[call.outcome] || OUTCOME_CONFIG[call.status] || {};
                  const lead = call.leadId || {};
                  return (
                    <div key={call._id}
                      onClick={() => nav(`/ai-dialer/calls/${call._id}`)}
                      className="flex items-center gap-3 p-3 bg-civix-panelLight rounded-xl border border-civix-border hover:border-civix-purple/30 cursor-pointer transition-all">
                      <div className="w-9 h-9 rounded-full bg-civix-purple/20 flex items-center justify-center shrink-0">
                        <Phone className="w-4 h-4 text-civix-purple" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-white">{lead.firstName} {lead.lastName}</div>
                        <div className="text-xs text-white/40">{lead.phone} · {lead.state}</div>
                      </div>
                      <div className={`text-xs font-medium px-2 py-0.5 rounded-full ${oc.bg || 'bg-white/5'} ${oc.color || 'text-white/50'}`}>
                        {oc.label || call.status}
                      </div>
                      <div className="text-xs text-white/40">
                        {call.durationSeconds ? `${call.durationSeconds}s` : '—'}
                      </div>
                      <div className="text-xs text-white/30">
                        {call.costBreakdown?.total ? fmt(call.costBreakdown.total) : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
